import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { LearningCharts } from "@/components/LearningCharts";
import { BadgeMedal } from "@/components/BadgeMedal";
import StreakRescueBanner from "@/components/StreakRescueBanner";
import { CheckCircle2, XCircle, Target, Layers, ArrowRight, Sparkles, Download, Share2, Flame, Award, BarChart3, ClipboardList } from "lucide-react";
import AccessBanner from "@/components/AccessBanner";
import LimitedAccessTour from "@/components/LimitedAccessTour";
import ProfileIncompleteBanner from "@/components/ProfileIncompleteBanner";
import NoEnrolledCourseBanner from "@/components/NoEnrolledCourseBanner";
import FriendUpdatesCard from "@/components/FriendUpdatesCard";
import DailyLoadBanner from "@/components/DailyLoadBanner";
import TroubleCardsWidget from "@/components/TroubleCardsWidget";
import ForgettingCurveDialog from "@/components/ForgettingCurveDialog";
import OnboardingModal from "@/components/OnboardingModal";
import StudyingNowRibbon from "@/components/StudyingNowRibbon";
import PastSelfDelta from "@/components/PastSelfDelta";
import TodayFocusCard from "@/components/TodayFocusCard";
import GlassmorphismStreakHero from "@/components/GlassmorphismStreakHero";
import ForgettingCurveDailyGame from "@/components/ForgettingCurveDailyGame";
import { DashboardSkeleton } from "@/components/skeletons";
import { toast } from "sonner";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [smart, setSmart] = useState(null);
  const [charts, setCharts] = useState(null);
  const [accessState, setAccessState] = useState(null);
  const [curveOpenQuestionId, setCurveOpenQuestionId] = useState(null);

  useEffect(() => {
    api.get("/student/dashboard").then((r) => setStats(r.data));
    api.get("/student/smart-review/summary").then((r) => setSmart(r.data)).catch(() => {});
    api.get("/student/learning-monitor/me").then((r) => setCharts(r.data)).catch(() => {});
    api.get("/student/access-status").then((r) => setAccessState(r.data)).catch(() => {});
  }, []);

  const exportReport = async () => {
    try {
      const token = localStorage.getItem("pm_token");
      const resp = await fetch(`${API}/student/learning-report/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `learning_report_${(user.full_name || "student").replace(/\s+/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Learning report downloaded");
    } catch {
      toast.error("Could not download report");
    }
  };

  if (!stats) return (
    <div className="space-y-8" data-testid="student-dashboard">
      <DashboardSkeleton />
    </div>
  );

  const hasChartData = charts && (
    (charts.accuracy_by_subject || []).length > 0 ||
    (charts.accuracy_trend || []).length > 0 ||
    (charts.mastery_distribution || []).some((d) => d.count > 0)
  );

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      <OnboardingModal />
      <StudyingNowRibbon />
      <LimitedAccessTour />
      {accessState && !accessState.has_enrolled_course ? (
        <NoEnrolledCourseBanner />
      ) : (
        <>
          <ProfileIncompleteBanner next="/student" />
          <AccessBanner compact={true} />
        </>
      )}

      <TodayFocusCard />

      {/* Hero + streak/game grid.
          Full-width single-column up to xl: (covers iPad landscape at
          1024px so the "Don't break the chain" and streak cards get the
          full page width instead of being squeezed into a narrow
          right-hand column). Two-column layout only kicks in on true
          desktop widths >= 1280px. */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start" data-testid="dashboard-grid">
        <div className="xl:col-span-8 flex flex-col gap-6 min-w-0" data-testid="dashboard-main-col">
          <DailyLoadBanner />
          <PastSelfDelta />
          <TroubleCardsWidget limit={5} onOpenCurve={(qid) => setCurveOpenQuestionId(qid)} />
        </div>
        <aside className="xl:col-span-4 flex flex-col gap-6 min-w-0" data-testid="dashboard-sidebar-col">
          {stats.streak && (
            <GlassmorphismStreakHero stats={stats} />
          )}
          <ForgettingCurveDailyGame />
        </aside>
      </div>

      <ForgettingCurveDialog
        questionId={curveOpenQuestionId}
        open={!!curveOpenQuestionId}
        onClose={() => setCurveOpenQuestionId(null)}
      />
      {/* Greeting row: stacks (greeting on top, action buttons below) up
          to xl: so on iPad the "Hi, Allan" block gets the full row width
          instead of being squeezed into a narrow left column. */}
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4" data-testid="dashboard-welcome-hero">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
            {(() => {
              const h = new Date().getHours();
              const emoji = h < 12 ? "☀️" : h < 18 ? "🌤️" : "🌙";
              const label = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
              return <><span>{emoji}</span> {label}</>;
            })()}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3 flex-wrap">
            <span>Hi, {user.full_name.split(" ")[0]}</span>
            {stats.streak && stats.streak.current_streak > 0 && (
              <span
                className={`inline-flex items-center gap-1 text-base font-bold px-2.5 py-1 rounded-full border ${stats.streak.active_today ? "bg-orange-100 text-orange-800 border-orange-300" : "bg-slate-100 text-slate-700 border-slate-300"}`}
                title={stats.streak.active_today ? "On fire today!" : "Answer a question today to keep it going"}
                data-testid="hero-streak-chip"
              >
                <Flame className="w-4 h-4" strokeWidth={2} />
                {stats.streak.current_streak}-day streak
              </span>
            )}
          </h1>
          <p className="text-slate-600 mt-1.5">
            {(() => {
              const due = smart?.due_today || 0;
              if (due === 0 && stats.total_answered > 0) return "You're all caught up — great work. Get ahead with an extra session.";
              if (due > 0) return `${due} card${due === 1 ? "" : "s"} due for review today — short sessions beat cramming.`;
              if (stats.total_answered === 0) return "Ready to start? Answer your first question to kick off your streak.";
              return "Here's your review progress so far.";
            })()}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/student/analytics">
            <Button variant="outline" data-testid="dashboard-analytics-btn">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </Button>
          </Link>
          <Link to="/student/enrollments">
            <Button variant="outline" data-testid="dashboard-enrollments-btn">
              <ClipboardList className="w-4 h-4 mr-2" /> Enrollments
            </Button>
          </Link>
          <Link to="/student/progress-card">
            <Button variant="outline" data-testid="my-progress-card-btn">
              <Share2 className="w-4 h-4 mr-2" /> My Progress Card
            </Button>
          </Link>
          <Button variant="outline" onClick={exportReport} data-testid="student-export-report-btn">
            <Download className="w-4 h-4 mr-2" /> Export Learning Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Target, label: "Questions Answered", value: stats.total_answered },
          { icon: CheckCircle2, label: "Correct", value: stats.correct, color: "green" },
          { icon: XCircle, label: "Incorrect", value: stats.incorrect, color: "red" },
          { icon: Layers, label: "Modules Attempted", value: stats.modules_attempted },
        ].map((s, i) => (
          <Card key={i} className="p-6 border-slate-200">
            <div className={`h-10 w-10 rounded-md grid place-items-center bg-${s.color || "blue"}-50 text-${s.color || "blue"}-700`}><s.icon className="w-5 h-5" strokeWidth={1.5} /></div>
            <div className="mt-4 text-3xl font-display font-bold">{s.value}</div>
            <div className="text-sm text-slate-500 mt-1">{s.label}</div>
          </Card>
        ))}
      </div>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Overall Score</h3>
          <span className="text-3xl font-display font-bold text-blue-700">{stats.score_percentage}%</span>
        </div>
        <Progress value={stats.score_percentage} className="mt-4 h-3" />
        <p className="text-sm text-slate-500 mt-2">{stats.correct} of {stats.total_answered} answered correctly</p>
      </Card>

      {stats.streak && (
        <StreakRescueBanner
          stats={stats}
          onFrozen={() => api.get("/student/dashboard").then((r) => setStats(r.data))}
        />
      )}

      {stats.streak && (
        <Card
          className="p-6 border-amber-200 bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50 relative overflow-hidden"
          data-testid="streak-card"
        >
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-200/40 blur-2xl pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full grid place-items-center shadow-sm ${stats.streak.active_today ? "bg-gradient-to-br from-orange-500 to-rose-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                <Flame className="w-7 h-7" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-700">Daily Streak</div>
                <div className="font-display text-3xl font-bold tracking-tight mt-1" data-testid="streak-value">
                  {stats.streak.current_streak} <span className="text-base font-semibold text-slate-600">{stats.streak.current_streak === 1 ? "day" : "days"}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {stats.streak.active_today
                    ? "You're on fire today — keep the chain alive!"
                    : stats.streak.current_streak > 0
                    ? "Answer a question today to keep your streak going."
                    : stats.total_answered > 0
                    ? "Your streak reset. Start a new one today!"
                    : "Answer your first question to start your streak."}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Best Streak</div>
              <div className="font-display text-2xl font-bold text-slate-800" data-testid="streak-longest">{stats.streak.longest_streak}</div>
            </div>
          </div>
        </Card>
      )}

      <FriendUpdatesCard />


      {smart && smart.has_data && (
        (() => {
          const due = smart.due_today || 0;
          const allCaughtUp = due === 0;
          // Urgency-driven palette — escalates from emerald (calm) → amber → rose
          const palette = allCaughtUp
            ? { ring: "ring-emerald-200", bg: "from-emerald-50 to-teal-50", num: "text-emerald-600", chip: "bg-emerald-100 text-emerald-800 border-emerald-300" }
            : due > 50
              ? { ring: "ring-rose-200", bg: "from-rose-50 to-orange-50", num: "text-rose-600", chip: "bg-rose-100 text-rose-800 border-rose-300" }
              : due > 20
                ? { ring: "ring-amber-200", bg: "from-amber-50 to-yellow-50", num: "text-amber-600", chip: "bg-amber-100 text-amber-800 border-amber-300" }
                : { ring: "ring-indigo-200", bg: "from-indigo-50 to-blue-50", num: "text-indigo-700", chip: "bg-indigo-100 text-indigo-800 border-indigo-300" };
          return (
            <Card className={`p-6 border-slate-200 bg-gradient-to-br ${palette.bg}`} data-testid="smart-review-card">
              <div className="flex items-stretch justify-between gap-6 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Smart Review · FSRS
                  </div>
                  <h3 className="font-display font-bold text-xl mt-1">
                    {allCaughtUp
                      ? "🎉 All caught up — great work!"
                      : `${due} card${due === 1 ? "" : "s"} due for review today`}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {allCaughtUp
                      ? "No reviews scheduled right now. Get ahead by starting an extra session."
                      : "Stay on top of your spaced repetition queue — short daily sessions beat marathon cramming."}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-sm">
                    {smart.weakest_subject && (
                      <Badge variant="outline" className="bg-white">
                        Weakest: {smart.weakest_subject.name} ({smart.weakest_subject.accuracy}%)
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-white">Accuracy: {smart.accuracy}%</Badge>
                    {smart.mastered > 0 && (
                      <Badge variant="outline" className="bg-white">Mastered: {smart.mastered}</Badge>
                    )}
                    {smart.repeatedly_missed > 0 && (
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                        {smart.repeatedly_missed} repeated misses
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Hero "Cards Due Today" badge — Anki-style daily target */}
                <div className="flex items-center gap-4">
                  <div
                    className={`relative grid place-items-center min-w-[112px] h-[112px] rounded-2xl bg-white shadow-sm ring-4 ${palette.ring}`}
                    data-testid="cards-due-today-badge"
                  >
                    <div className="text-center px-2">
                      <div className={`font-display font-extrabold text-5xl leading-none tabular-nums ${palette.num}`} data-testid="cards-due-today-count">
                        {due}
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mt-1">
                        Due today
                      </div>
                    </div>
                  </div>
                  <Link to="/student/smart-review">
                    <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="dashboard-smart-review-btn">
                      {allCaughtUp ? "Get Ahead" : "Start Smart Review"} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          );
        })()
      )}

      {stats.badges_earned && stats.badges_earned.length > 0 && (
        <Card className="p-6 border-slate-200" data-testid="dashboard-badges-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-500" /> Recent Badges
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">{stats.badges_earned.length} of {stats.badges_total} unlocked</p>
            </div>
            <Link to="/student/badges" className="text-sm text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-5">
            {stats.badges_earned.slice(0, 6).map((b) => (
              <div key={b.id} className="flex flex-col items-center gap-1 w-20" data-testid={`dash-badge-${b.id}`}>
                <BadgeMedal badge={b} size="md" />
                <div className="text-[11px] font-semibold text-center text-slate-700 leading-tight">{b.name}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {hasChartData && (
        <div data-testid="student-charts-section">
          <h3 className="font-display font-bold text-lg mb-3">Your Learning Insights</h3>
          <LearningCharts data={charts} testIdPrefix="student-" />
        </div>
      )}

      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg">Ready to keep going?</h3>
            <p className="text-sm text-slate-600">Pick a course and start your review session.</p>
          </div>
          <Link to="/student/courses" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1E3A8A] text-white hover:bg-[#1E40AF] text-sm font-semibold" data-testid="browse-courses-btn">
            Browse Courses <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Card>
    </div>
  );
}
